import { type FunctionComponent } from "react";

import { AlertType } from "@ppb/the-wall-common/types";
import { BetSegmentsSize } from "../BetSegments/BetSegments.types";
import { type BetSummaryProps } from "./BetSummary.types";
import classnames from "classnames";

import { Option } from "@ppb/the-wall-web/components/walls/Option/Option";
import { FreeBets } from "@ppb/the-wall-web/components/walls/FreeBets/FreeBets";
import { AccaInsurance } from "@ppb/the-wall-web/components/rooms/AccaInsurance/AccaInsurance";
import { BetSegments } from "../BetSegments/BetSegments.web";
import { Alert } from "@ppb/the-wall-web/components/bricks/Alert/Alert";

import styles from "./BetSummary.web.css";

type BetResult = {
  label: string;
  value: string;
};

type BetResultProps = {
  lines: BetSummaryProps["lines"];
  odds: BetSummaryProps["odds"];
  labels: Pick<BetSummaryProps["i18n"], "oddsLabel" | "linesLabel">;
};

const getBetResult = ({ lines, odds, labels: { linesLabel, oddsLabel } }: BetResultProps): BetResult => {
  const hasSeveralLines = lines && lines > 1;

  if (hasSeveralLines && linesLabel) {
    return {
      label: linesLabel,
      value: `${lines}`,
    };
  }

  return {
    label: oddsLabel,
    value: odds,
  };
};

export const BetSummary: FunctionComponent<BetSummaryProps> = ({
  title,
  odds,
  stake,
  returns,
  i18n: { eachWayLabel, accaInsuranceLabel, stakeLabel, oddsLabel, returnsLabel, linesLabel },
  hasAccaInsurance = false,
  hasBonusUsed = false,
  freeBetsLabel,
  generosityAlertMessage,
  hasEachWay = false,
  lines,
  hasShownReceiptIds = false,
  hasMyOddsBoost = false,
  previousValue,
  previousOdds,
  generosityIconName,
}) => {
  const containerStyles = classnames(styles.container, {
    [styles.noPaddingBottom]: hasShownReceiptIds,
  });
  const hasAnyBonus = hasBonusUsed || generosityAlertMessage;
  const { label, value } = getBetResult({ lines, odds, labels: { linesLabel, oddsLabel } });

  return (
    <div>
      <article className={containerStyles}>
        {!!title && <h5 className={styles.title}>{title}</h5>}
        {!!hasEachWay && eachWayLabel && <Option title={eachWayLabel} isSelected isReadOnly />}
        <BetSegments
          leftLabel={label}
          leftValue={value}
          leftPreviousValue={previousOdds}
          midLabel={stakeLabel}
          midValue={stake}
          rightLabel={returnsLabel}
          rightValue={returns}
          rightPreviousValue={previousValue}
          size={BetSegmentsSize.SMALL}
          isOddsBoosted={hasMyOddsBoost}
        />
        {hasAccaInsurance && accaInsuranceLabel && (
          <div className={styles.accaInsurance}>
            <AccaInsurance title={accaInsuranceLabel} isSelected={true} isReadOnly={true} />
          </div>
        )}
        {hasAnyBonus && (
          <>
            {!!generosityAlertMessage && (
              <Alert
                message={generosityAlertMessage}
                iconOverload={generosityIconName}
                type={AlertType.Generosity}
                showCloseIcon={false}
              />
            )}
            {!!freeBetsLabel && (
              <div className={styles.option}>
                <FreeBets label={freeBetsLabel} isReadOnly isSelected={hasBonusUsed} />
              </div>
            )}
          </>
        )}
      </article>
    </div>
  );
};
